import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Loader2, Check, Eye, Pencil, Trash2, RotateCcw, Info } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULE_GROUPS = [
  { label: 'Analytics & Dashboard', modules: ['intelligence', 'dashboard', 'funnel_analytics'] },
  { label: 'Sales & Leads', modules: ['leads', 'import_leads', 'field_data', 'tags', 'products'] },
  { label: 'Activities', modules: ['events', 'appointments', 'tasks', 'communications', 'calendar'] },
  { label: 'Finance & Contracts', modules: ['customers', 'contracts', 'booking_forms', 'installments', 'payments'] },
  { label: 'Admin & Settings', modules: ['automation', 'landing_pages', 'user_requests', 'accounting', 'users'] },
];

const ACTION_DEFS = {
  intelligence: ['view'], dashboard: ['view'], funnel_analytics: ['view'],
  leads: ['view', 'edit', 'delete'], import_leads: ['view', 'edit'], field_data: ['view', 'edit'],
  tags: ['view', 'edit', 'delete'], products: ['view', 'edit', 'delete'],
  events: ['view', 'edit', 'delete'], appointments: ['view', 'edit', 'delete'],
  tasks: ['view', 'edit', 'delete'], communications: ['view', 'edit'], calendar: ['view', 'edit', 'delete'],
  customers: ['view', 'edit', 'delete'], contracts: ['view', 'edit', 'delete'],
  booking_forms: ['view', 'edit'], installments: ['view', 'edit', 'delete'],
  payments: ['view', 'edit', 'delete'], automation: ['view', 'edit'],
  landing_pages: ['view', 'edit', 'delete'], user_requests: ['view', 'edit'],
  accounting: ['view', 'edit'], users: ['view', 'edit'],
};

const MODULE_LABELS = {
  intelligence: 'Intelligence', dashboard: 'Dashboard', funnel_analytics: 'Funnel Analytics',
  leads: 'Leads', import_leads: 'Import Leads', field_data: 'Field Data', tags: 'Tags', products: 'Products',
  events: 'Events', appointments: 'Appointments', tasks: 'Tasks', communications: 'Communications', calendar: 'Calendar',
  customers: 'Customers', contracts: 'Contracts', booking_forms: 'Booking Forms',
  installments: 'Installments', payments: 'Payments', automation: 'Automation',
  landing_pages: 'Landing Pages', user_requests: 'User Requests', accounting: 'Accounting', users: 'Users',
};

export default function UserPermissionsModal({ open, onOpenChange, targetUser, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roleModules, setRoleModules] = useState({});
  const [overrides, setOverrides] = useState({});
  const [originalOverrides, setOriginalOverrides] = useState({});

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const fetchData = useCallback(async () => {
    if (!targetUser) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/user-permissions/${targetUser.user_id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRoleModules(data.role_modules || {});
        setOverrides({ ...data.user_overrides });
        setOriginalOverrides({ ...data.user_overrides });
      }
    } catch (err) {
      logger.error('Failed to load user permissions:', err);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [targetUser]);

  useEffect(() => {
    if (open && targetUser) fetchData();
  }, [open, targetUser, fetchData]);

  const getEffective = (moduleId) => {
    if (overrides[moduleId] !== undefined) return overrides[moduleId];
    return roleModules[moduleId] || { enabled: false, view: false, edit: false, delete: false };
  };

  const isOverridden = (moduleId) => overrides[moduleId] !== undefined;

  const toggleOverride = (moduleId, field) => {
    const current = getEffective(moduleId);
    const updated = { ...current };

    if (field === 'enabled') {
      updated.enabled = !updated.enabled;
      if (!updated.enabled) {
        updated.view = false; updated.edit = false; updated.delete = false;
      } else {
        updated.view = true;
      }
    } else {
      updated[field] = !updated[field];
      if (field === 'view' && !updated.view) {
        updated.edit = false; updated.delete = false;
      }
      if ((field === 'edit' || field === 'delete') && updated[field]) {
        updated.view = true;
      }
    }

    setOverrides(prev => ({ ...prev, [moduleId]: updated }));
  };

  const clearOverride = (moduleId) => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[moduleId];
      return next;
    });
  };

  const clearAllOverrides = async () => {
    try {
      const res = await fetch(`${API_URL}/api/user-permissions/${targetUser.user_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        setOverrides({});
        setOriginalOverrides({});
        toast.success('All custom permissions cleared');
        onSaved?.();
      }
    } catch {
      toast.error('Failed to clear overrides');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/user-permissions/${targetUser.user_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modules: overrides }),
      });
      if (res.ok) {
        toast.success(`Custom permissions saved for ${targetUser.name}`);
        setOriginalOverrides({ ...overrides });
        onSaved?.();
        onOpenChange(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const overrideCount = Object.keys(overrides).length;
  const hasChanges = JSON.stringify(overrides) !== JSON.stringify(originalOverrides);

  if (!targetUser) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="user-permissions-modal">
        <DialogHeader>
          <DialogTitle className="text-lg">Customize Permissions</DialogTitle>
          <DialogDescription>
            Override role defaults for this specific user
          </DialogDescription>
        </DialogHeader>

        {/* User info */}
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border border-slate-200">
              <AvatarImage src={targetUser.picture} alt={targetUser.name} />
              <AvatarFallback className="bg-gold/10 text-gold">{getInitials(targetUser.name)}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-sm">{targetUser.name}</p>
              <p className="text-xs text-slate-500">{targetUser.email} — {targetUser.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {overrideCount > 0 && (
              <Badge className="bg-amber-100 text-amber-800 text-xs">{overrideCount} override{overrideCount !== 1 ? 's' : ''}</Badge>
            )}
            {overrideCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAllOverrides} className="text-xs text-red-500 hover:text-red-700" data-testid="clear-all-overrides">
                <RotateCcw size={12} className="mr-1" /> Reset All
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500 px-1">
          <Info size={12} />
          <span>Overridden modules show in <span className="text-amber-600 font-medium">amber</span>. Toggle modules ON/OFF to grant or revoke access beyond the role default.</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            <span className="ml-2 text-sm text-slate-500">Loading permissions...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {MODULE_GROUPS.map(group => (
              <div key={group.label} className="border rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b">
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{group.label}</p>
                </div>
                <div className="divide-y">
                  {group.modules.map(moduleId => {
                    const eff = getEffective(moduleId);
                    const isOvr = isOverridden(moduleId);
                    const roleBase = roleModules[moduleId] || {};
                    const actions = ACTION_DEFS[moduleId] || ['view'];
                    const hasEdit = actions.includes('edit');
                    const hasDelete = actions.includes('delete');
                    const diffFromRole = isOvr && eff.enabled !== (roleBase.enabled || false);

                    return (
                      <div
                        key={moduleId}
                        className={`flex items-center justify-between px-3 py-2.5 ${isOvr ? 'bg-amber-50/50' : ''} ${!eff.enabled ? 'opacity-60' : ''}`}
                        data-testid={`user-module-${moduleId}`}
                      >
                        <div className="flex items-center gap-2.5 min-w-[170px]">
                          <Switch
                            checked={eff.enabled}
                            onCheckedChange={() => toggleOverride(moduleId, 'enabled')}
                            data-testid={`user-toggle-${moduleId}`}
                          />
                          <span className={`text-sm font-medium ${eff.enabled ? 'text-slate-900' : 'text-slate-400'}`}>
                            {MODULE_LABELS[moduleId]}
                          </span>
                          {isOvr && (
                            <button
                              onClick={() => clearOverride(moduleId)}
                              className="text-[10px] text-amber-600 hover:text-amber-800 underline"
                              title="Remove override, revert to role default"
                              data-testid={`user-clear-${moduleId}`}
                            >
                              reset
                            </button>
                          )}
                        </div>

                        {eff.enabled && (
                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-1 cursor-pointer select-none">
                              <input type="checkbox" checked={eff.view} onChange={() => toggleOverride(moduleId, 'view')}
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5" />
                              <Eye size={12} className="text-slate-500" />
                              <span className="text-[11px] text-slate-600">View</span>
                            </label>
                            {hasEdit && (
                              <label className="flex items-center gap-1 cursor-pointer select-none">
                                <input type="checkbox" checked={eff.edit} onChange={() => toggleOverride(moduleId, 'edit')}
                                  className="rounded border-slate-300 text-amber-600 focus:ring-amber-500 h-3.5 w-3.5" />
                                <Pencil size={12} className="text-slate-500" />
                                <span className="text-[11px] text-slate-600">Edit</span>
                              </label>
                            )}
                            {hasDelete && (
                              <label className="flex items-center gap-1 cursor-pointer select-none">
                                <input type="checkbox" checked={eff.delete} onChange={() => toggleOverride(moduleId, 'delete')}
                                  className="rounded border-slate-300 text-red-600 focus:ring-red-500 h-3.5 w-3.5" />
                                <Trash2 size={12} className="text-slate-500" />
                                <span className="text-[11px] text-slate-600">Delete</span>
                              </label>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Save footer */}
        <div className="flex items-center justify-between pt-3 border-t">
          <p className="text-xs text-slate-500">
            {overrideCount === 0 ? 'No custom overrides — using role defaults' : `${overrideCount} module${overrideCount !== 1 ? 's' : ''} customized`}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving || !hasChanges} data-testid="save-user-permissions">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
